import { useState, useEffect, useCallback } from 'react';

const BITRIX_API_URL = process.env.REACT_APP_BITRIX_URL || '';

/**
 * Простой хук для загрузки всех данных из Битрикс без фильтрации
 */
export function useSimpleDataLoader() {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [fieldMetadata, setFieldMetadata] = useState({});

  // Функция получения названия стадии
  const getStageNameById = useCallback((stageId) => {
    const stages = {
      // Категория 6
      'DT1036_6:NEW': 'Новая заявка',
      'DT1036_6:UC_58B1IG': 'Резка стекла',
      'DT1036_6:UC_HD3BPN': 'Обработка стекла',
      'DT1036_6:UC_PBEINX': 'На закалке',
      'DT1036_6:UC_4FXLEO': 'Матировка',
      'DT1036_6:UC_WE1KJE': 'Сборка лофта',
      'DT1036_6:UC_SNLWF9': 'Упаковка',
      'DT1036_6:UC_ZZP9IY': 'Комплектация',
      'DT1036_6:SUCCESS': 'Выполнен',
      
      // Категория 7  
      'DT1036_7:NEW': 'Новая заявка',
      'DT1036_7:UC_58B1IG': 'Резка стекла',
      'DT1036_7:UC_HD3BPN': 'Обработка стекла',
      'DT1036_7:UC_PBEINX': 'На закалке',
      'DT1036_7:UC_4FXLEO': 'Матировка',
      'DT1036_7:UC_WE1KJE': 'Сборка лофта',
      'DT1036_7:UC_SNLWF9': 'Упаковка',
      'DT1036_7:UC_ZZP9IY': 'Комплектация',
      'DT1036_7:SUCCESS': 'Выполнен'
    };
    return stages[stageId] || `Неизвестная стадия (${stageId})`;
  }, []);

  // Загрузка метаданных полей
  const loadFieldMetadata = useCallback(async () => {
    try {
      console.log('🔍 Загружаем метаданные полей...');
      
      const response = await fetch(
        `${BITRIX_API_URL}crm.item.fields?entityTypeId=1036`
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (data.result) {
        console.log('📋 Метаданные полей получены:', {
          fieldsCount: Object.keys(data.result).length,
          sampleFields: Object.keys(data.result).slice(0, 5),
          fullData: data.result
        });
        setFieldMetadata(data.result);
      }
    } catch (err) {
      console.error('Ошибка загрузки метаданных полей:', err);
    }
  }, []);

  // Загрузка данных с возможностью указания периода
  const loadAllData = useCallback(async (dateRange = null) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🚀 Начинаем загрузку данных...', dateRange ? `Период: ${dateRange.from} - ${dateRange.to}` : 'За все время');
      
      const allItems = [];
      let currentStart = 0;
      const batchSize = 50; // Стандартный размер батча для Bitrix24 API
      let hasMore = true;

      while (hasMore) {
        // Строим параметры запроса
        const params = new URLSearchParams({
          entityTypeId: '1036',
          start: currentStart,
          limit: batchSize
        });

        // Добавляем категории
        params.append('filter[categoryId]', '6');
        params.append('filter[categoryId]', '7');

        // Добавляем фильтры по дате, если указаны
        if (dateRange) {
          if (dateRange.from) {
            params.append('filter[>createdTime]', dateRange.from + 'T00:00:00');
          }
          if (dateRange.to) {
            params.append('filter[<createdTime]', dateRange.to + 'T23:59:59');
          }
        }

        // Добавляем все поля для выборки (включая все новые поля)
        const selectFields = [
          // Стандартные поля
          'id', 'title', 'stageId', 'createdTime', 'updatedTime',
          'assignedById', 'opportunity', 'companyId', 'contactId', 'createdBy',
          
          // Пользовательские поля (полный список)
          'ufCrm3_1746789101', 'ufCrm3_1746789363', 'ufCrm3_1746789446', 'ufCrm3_1746789468', 
          'ufCrm3_1746789474', 'ufCrm3_1746789572', 'ufCrm3_1746789589', 'ufCrm3_1746789599', 
          'ufCrm3_1746789627', 'ufCrm3_1746789650', 'ufCrm3_1746789862', 'ufCrm3_1746789890', 
          'ufCrm3_1746789910', 'ufCrm3_1746790033', 'ufCrm3_1746790136', 'ufCrm3_1746790327',
          'ufCrm3_1746790893', 'ufCrm3_1746791181', 'ufCrm3_1747750785257', 'ufCrm3_1749212561933',
          'ufCrm3_1749212579499', 'ufCrm3_1749212591302', 'ufCrm3_1749212635898', 'ufCrm3_1749212685683',
          'ufCrm3_1749212694990', 'ufCrm3_1749212719306', 'ufCrm3_1749212970874', 'ufCrm3_1749212997464',
          'ufCrm3_1749213049974', 'ufCrm3_1749213064008', 'ufCrm3_1749213221666', 'ufCrm3_1749213244986',
          'ufCrm3_1749213340553', 'ufCrm3_1749213350972', 'ufCrm3_1749213376934', 'ufCrm3_1749475648149',
          'ufCrm3_1749555592378', 'ufCrm3_1751274844238', 'ufCrm3_1751274861103', 'ufCrm3_1751275030885',
          'ufCrm3_1751275041994', 'ufCrm3_1751275363654', 'ufCrm3_1751275374960', 'ufCrm3_1751279653655',
          'ufCrm3_1751344784096', 'ufCrm3_1751344871698', 'ufCrm3_1751344928901', 'ufCrm3_1751370238766',
          'ufCrm3_1751370309345', 'ufCrm3_1751370446708',
          
          // Новые поля (добавляем недостающие из списка)
          'ufCrm3_1751456530577', 'ufCrm3_1751521756206', 'ufCrm3_1751521814271', 'ufCrm3_1751521846111',
          'ufCrm3_1752035088903', 'ufCrm3_1752035158464', 'ufCrm3_1752035904757', 'ufCrm3_1752122668020',
          'ufCrm3_1752145786785', 'ufCrm3_1752145842832', 'ufCrm3_1752165266', 'ufCrm3_1752818210443',
          'ufCrm3_1752818233225', 'ufCrm3_1752818259434', 'ufCrm3_1753076476775', 'ufCrm3_1753263371950',
          'ufCrm3_1753795707997', 'ufCrm3_1753799333731', 'ufCrm3_1753799343825', 'ufCrm3_1753862276063',
          'ufCrm3_1754027253985', 'ufCrm3_1754027309932', 'ufCrm3_1754027324300', 'ufCrm3_1754027335383',
          'ufCrm3_1754046893462', 'ufCrm3_1755579125099', 'ufCrm3_1755579396141', 'ufCrm3_1757005830',
          'ufCrm3_1757404689894'
        ];
        
        selectFields.forEach(field => {
          params.append('select[]', field);
        });

        // Делаем запрос
        const url = `${BITRIX_API_URL}crm.item.list?${params}`;
        console.log(`📡 Запрос к API: start=${currentStart}, limit=${batchSize}`);
        console.log(`📋 Запрашиваем полей: ${selectFields.length}`);
        if (currentStart === 0) {
          console.log(`🔍 Список запрашиваемых полей:`, selectFields);
        }
        console.log(`🔗 URL: ${url}`);
        
        const response = await fetch(url);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const data = await response.json();
        console.log(`📥 Ответ API:`, {
          itemsCount: data.result?.items?.length || 0,
          hasMore: data.more,
          total: data.total,
          nextStart: data.next
        });
        
        // Логируем первую запись для анализа структуры полей
        if (data.result?.items?.length > 0 && currentStart === 0) {
          const firstItem = data.result.items[0];
          console.log(`🔍 Структура первой записи:`, firstItem);
          
          // Анализируем какие поля пустые/заполненные
          const fieldAnalysis = {};
          Object.keys(firstItem).forEach(fieldId => {
            const value = firstItem[fieldId];
            fieldAnalysis[fieldId] = {
              hasValue: value !== null && value !== undefined && value !== '',
              type: typeof value,
              isArray: Array.isArray(value),
              value: Array.isArray(value) ? `Array(${value.length})` : value
            };
          });
          
          console.log(`📊 Анализ полей первой записи:`, fieldAnalysis);
          
          // Показываем только поля, которые не пустые
          const nonEmptyFields = Object.keys(fieldAnalysis).filter(fieldId => 
            fieldAnalysis[fieldId].hasValue
          );
          console.log(`✅ Поля с данными (${nonEmptyFields.length}):`, nonEmptyFields);

          // Специально проверяем поле таймлайна
          const timelineFieldId = 'ufCrm3_1757404689894';
          console.log(`⏱️ Значение поля таймлайна (${timelineFieldId}):`, firstItem[timelineFieldId]);
        }
        
        if (data.result && data.result.items) {
          // Обрабатываем полученные данные
          const processedItems = data.result.items.map(item => ({
            ...item, // Все поля как есть
            orderNumber: item.ufCrm3_1746789101 || 'Без номера',
            currentStage: getStageNameById(item.stageId)
          }));
          
          allItems.push(...processedItems);
          
          // Проверяем, есть ли еще данные (используем информацию от API)
          hasMore = data.more === true || (data.result.items.length === batchSize && data.next);
          currentStart += batchSize;
          
          console.log(`Загружено ${allItems.length} записей...`);
        } else {
          hasMore = false;
        }
      }

      setAllData(allItems);
      setLastLoaded(new Date());
      console.log(`✅ Загрузка завершена! Всего записей: ${allItems.length}`);
      
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getStageNameById]);

  // Автоматическая загрузка при монтировании компонента (по умолчанию за месяц)
  useEffect(() => {
    // Сначала загружаем метаданные полей
    loadFieldMetadata();
    
    const defaultRange = {
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      to: new Date().toISOString().split('T')[0]
    };
    loadAllData(defaultRange);
  }, [loadAllData, loadFieldMetadata]);

  // Функция для принудительного обновления данных с возможностью указания периода
  const refreshData = useCallback((dateRange = null) => {
    loadAllData(dateRange);
  }, [loadAllData]);

  return {
    allData,          // Все загруженные данные
    loading,          // Идет ли загрузка
    error,            // Ошибка загрузки
    lastLoaded,       // Время последней загрузки
    fieldMetadata,    // Метаданные полей из Bitrix24
    refreshData       // Функция для обновления данных
  };
}
